import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, Target, TrendingUp, FileText, Download, X, FileSpreadsheet, ArrowRight } from 'lucide-react';
import api, { DashboardStats, ChartData } from '../services/api';
import KPI from '../components/KPI';
import Charts from '../components/Charts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function ReportModal({ stats, chartData, onClose }: { stats: DashboardStats | null; chartData: ChartData[]; onClose: () => void }) {
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text('CRM Report', 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
    
    // Key Metrics
    doc.setFontSize(14);
    doc.text('Key Metrics', 14, 45);
    
    doc.setFontSize(10);
    doc.text(`Total Leads: ${stats?.totalLeads || 0} (Trend: ${stats?.leadsTrend || 0}%)`, 14, 55);
    doc.text(`Total Customers: ${stats?.totalCustomers || 0} (Trend: ${stats?.customersTrend || 0}%)`, 14, 62);
    doc.text(`Revenue: $${(stats?.revenue || 0).toLocaleString()} (Trend: ${stats?.revenueTrend || 0}%)`, 14, 69);
    doc.text(`Conversion Rate: ${stats?.conversionRate || 0}% (Trend: ${stats?.conversionTrend || 0}%)`, 14, 76);
    
    // Monthly Breakdown Table
    doc.setFontSize(14);
    doc.text('Monthly Breakdown', 14, 90);
    
    const tableData = chartData.map(d => [d.month, `$${d.revenue.toLocaleString()}`, d.leads.toString(), d.customers?.toString() || '0']);
    
    (doc as any).autoTable({
      head: [['Month', 'Revenue', 'Leads', 'Customers']],
      body: tableData,
      startY: 95,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });
    
    // Footer
    doc.setFontSize(8);
    doc.text('Report generated by SalesCRM', 14, (doc as any).lastAutoTable.finalY + 10);
    
    doc.save(`crm-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const handleDownloadExcel = () => {
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['CRM Report'],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [''],
      ['Key Metrics'],
      ['Metric', 'Value', 'Trend'],
      ['Total Leads', stats?.totalLeads || 0, `${stats?.leadsTrend || 0}%`],
      ['Total Customers', stats?.totalCustomers || 0, `${stats?.customersTrend || 0}%`],
      ['Revenue', `$${(stats?.revenue || 0).toLocaleString()}`, `${stats?.revenueTrend || 0}%`],
      ['Conversion Rate', `${stats?.conversionRate || 0}%`, `${stats?.conversionTrend || 0}%`],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    
    // Monthly Data sheet
    const monthlyData = chartData.map(d => ({
      Month: d.month,
      Revenue: d.revenue,
      Leads: d.leads,
      Customers: d.customers || 0,
    }));
    const wsMonthly = XLSX.utils.json_to_sheet(monthlyData);
    XLSX.utils.book_append_sheet(wb, wsMonthly, 'Monthly Data');
    
    XLSX.writeFile(wb, `crm-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const reportContent = `
CRM REPORT
Generated: ${new Date().toLocaleDateString()}
===========================================

KEY METRICS
-----------
Total Leads: ${stats?.totalLeads || 0}
  - Trend: ${stats?.leadsTrend || 0}%

Total Customers: ${stats?.totalCustomers || 0}
  - Trend: ${stats?.customersTrend || 0}%

Revenue: $${(stats?.revenue || 0).toLocaleString()}
  - Trend: ${stats?.revenueTrend || 0}%

Conversion Rate: ${stats?.conversionRate || 0}%
  - Trend: ${stats?.conversionTrend || 0}%

MONTHLY BREAKDOWN
-----------------
${chartData.map(d => `${d.month}: $${d.revenue.toLocaleString()} Revenue, ${d.leads} Leads`).join('\n')}

===========================================
Report generated by SalesCRM
  `.trim();

  const handleDownload = () => {
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Report Preview</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-auto max-h-[60vh]">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-gray-50 p-4 rounded-lg">
            {reportContent}
          </pre>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <FileText className="w-4 h-4" />
            Text
          </button>
          <button onClick={handleDownloadPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            <Download className="w-4 h-4" />
            PDF
          </button>
          <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, revenueData] = await Promise.all([
          api.getDashboardStats(),
          api.getRevenueData(),
        ]);
        setStats(statsData);
        setChartData(revenueData);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's your business overview.</p>
        </div>
        <button onClick={() => setShowReport(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium">
          <FileText className="w-4 h-4" />
          Generate Report
        </button>
      </div>

      {showReport && <ReportModal stats={stats} chartData={chartData} onClose={() => setShowReport(false)} />}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <button 
          onClick={() => navigate('/leads')}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 text-left group"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalLeads || 0}</p>
              <div className={`flex items-center mt-2 text-sm ${(stats?.leadsTrend || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                <span>{(stats?.leadsTrend || 0) > 0 ? '+' : ''}{stats?.leadsTrend || 0}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span>View leads</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </div>
        </button>

        <button 
          onClick={() => navigate('/customers')}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 text-left group"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalCustomers || 0}</p>
              <div className={`flex items-center mt-2 text-sm ${(stats?.customersTrend || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                <span>{(stats?.customersTrend || 0) > 0 ? '+' : ''}{stats?.customersTrend || 0}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
              <Target className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span>View customers</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </div>
        </button>

        <button 
          onClick={() => navigate('/pipeline')}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 text-left group"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${(stats?.revenue || 0).toLocaleString()}</p>
              <div className={`flex items-center mt-2 text-sm ${(stats?.revenueTrend || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                <span>{(stats?.revenueTrend || 0) > 0 ? '+' : ''}{stats?.revenueTrend || 0}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
              <DollarSign className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span>View pipeline</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </div>
        </button>

        <button 
          onClick={() => navigate('/leads')}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 text-left group"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.conversionRate || 0}%</p>
              <div className={`flex items-center mt-2 text-sm ${(stats?.conversionTrend || 0) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                <span>{(stats?.conversionTrend || 0) > 0 ? '+' : ''}{stats?.conversionTrend || 0}%</span>
                <span className="text-gray-400 ml-1">vs last month</span>
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span>View leads</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </div>
        </button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Charts data={chartData} />
        </div>
        
        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {[
              { text: 'New lead from TechCorp Inc', time: '2 hours ago', type: 'lead' },
              { text: 'Deal closed: $15,000', time: '5 hours ago', type: 'deal' },
              { text: 'Customer upgraded to Pro', time: '1 day ago', type: 'upgrade' },
              { text: 'Follow-up scheduled', time: '2 days ago', type: 'reminder' },
              { text: 'New customer signup', time: '3 days ago', type: 'customer' },
            ].map((activity, index) => (
              <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0">
                <div className={`w-2 h-2 mt-2 rounded-full ${
                  activity.type === 'deal' ? 'bg-emerald-500' :
                  activity.type === 'lead' ? 'bg-indigo-500' :
                  activity.type === 'upgrade' ? 'bg-amber-500' :
                  'bg-gray-400'
                }`} />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.text}</p>
                  <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
